import api from './api';
import { getMockData, delay } from './mock-data-loader';

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

/**
 * Public API client for User Story 1
 * All endpoints are accessible without authentication
 */

/**
 * Get all competitions
 * @returns {Promise<{competitions: Array, count: number}>}
 */
export const getCompetitions = async () => {
  if (USE_MOCK_DATA) {
    const { mockCompetitions } = await getMockData();
    await delay();

    return {
      competitions: mockCompetitions.map(comp => ({
        ...comp,
        competitionId: comp.id, // Map id to competitionId for consistency
      })),
      count: mockCompetitions.length,
    };
  }

  const response = await api.get('/public/competitions');
  return response;
};

/**
 * Get competition details including team counts by division
 * @param {string} competitionId - Competition ID
 * @returns {Promise<{competition: Object, teamCount: number, divisionTeamCounts: Object}>}
 */
export const getCompetitionDetails = async (competitionId) => {
  if (USE_MOCK_DATA) {
    const { mockCompetitions, mockTeams } = await getMockData();
    await delay();

    const competition = mockCompetitions.find(c => c.id === competitionId);
    if (!competition) {
      throw new Error('Competition not found');
    }

    const teams = mockTeams.filter(t => t.competitionId === competitionId);
    const divisionTeamCounts = {};
    teams.forEach(team => {
      divisionTeamCounts[team.division] = (divisionTeamCounts[team.division] || 0) + 1;
    });

    // Convert divisions array to objects with divisionId and name
    const divisionsWithIds = competition.divisions.map(div => ({
      divisionId: div,
      name: `Division ${div}`,
    }));

    return {
      competition: {
        ...competition,
        competitionId: competition.id,
        divisions: divisionsWithIds,
      },
      teamCount: teams.length,
      divisionTeamCounts,
    };
  }

  const response = await api.get(`/public/competitions/${competitionId}`);
  return response;
};

/**
 * Get standings for a competition, optionally filtered by division
 * @param {string} competitionId - Competition ID
 * @param {string} [divisionId] - Optional division ID to filter standings
 * @returns {Promise<{standings: Array, count: number}>}
 */
export const getStandings = async (competitionId, divisionId = null) => {
  if (USE_MOCK_DATA) {
    const { mockTeams } = await getMockData();
    await delay();

    let teams = mockTeams.filter(t => t.competitionId === competitionId);

    if (divisionId) {
      teams = teams.filter(t => t.division === divisionId);
    }

    // Create standings from teams
    const standings = teams
      .map((team, index) => {
        const matchesPlayed = team.wins + team.losses;
        const winPercentage = matchesPlayed > 0
          ? ((team.wins / matchesPlayed) * 100).toFixed(1)
          : '0.0';

        return {
          teamId: team.id,
          teamName: team.name,
          division: team.division,
          matchesWon: team.wins,
          matchesLost: team.losses,
          gamesWon: team.wins * 7, // Approximate: 7 games per match won
          gamesLost: team.losses * 7, // Approximate: 7 games per match lost
          points: team.wins * 2, // 2 points per win
          rank: index + 1,
          matchesPlayed,
          winPercentage,
        };
      })
      .sort((a, b) => b.matchesWon - a.matchesWon);

    return {
      standings,
      count: standings.length,
    };
  }

  const params = divisionId ? `?divisionId=${divisionId}` : '';
  const response = await api.get(`/public/competitions/${competitionId}/standings${params}`);
  return response;
};

/**
 * Get match details with game-by-game results (completed matches only)
 * @param {string} matchId - Match ID
 * @returns {Promise<{match: Object, games: Array, team1: Object, team2: Object}>}
 */
export const getMatchDetails = async (matchId) => {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();

    const match = mockMatches.find(m => m.id === matchId);
    if (!match) {
      throw new Error('Match not found');
    }

    const team1 = mockTeams.find(t => t.id === match.team1Id);
    const team2 = mockTeams.find(t => t.id === match.team2Id);

    return {
      match: { ...match, matchId: match.id },
      games: match.games || [],
      team1,
      team2,
    };
  }

  const response = await api.get(`/public/matches/${matchId}`);
  return response;
};

/**
 * Get team profile with roster and statistics
 * @param {string} teamId - Team ID
 * @returns {Promise<{team: Object, players: Array, stats: Object, recentMatches: Array}>}
 */
export const getTeamProfile = async (teamId) => {
  if (USE_MOCK_DATA) {
    const { mockTeams, mockPlayers, mockMatches } = await getMockData();
    await delay();

    const team = mockTeams.find(t => t.id === teamId);
    if (!team) {
      throw new Error('Team not found');
    }

    const players = mockPlayers.filter(p => team.playerIds.includes(p.id));
    const recentMatches = mockMatches.filter(
      m => m.team1Id === teamId || m.team2Id === teamId
    ).slice(0, 5);

    const matchesPlayed = team.wins + team.losses;
    const winPercentage = matchesPlayed > 0
      ? ((team.wins / matchesPlayed) * 100).toFixed(1)
      : '0.0';

    return {
      team: { ...team, teamId: team.id },
      players,
      stats: {
        wins: team.wins,
        losses: team.losses,
        winPercentage,
      },
      recentMatches,
    };
  }

  const response = await api.get(`/public/teams/${teamId}`);
  return response;
};

/**
 * Get player statistics
 * @param {string} playerId - Player ID
 * @returns {Promise<{player: Object, team: Object, stats: Object}>}
 */
export const getPlayerStats = async (playerId) => {
  if (USE_MOCK_DATA) {
    const { mockPlayers, mockTeams } = await getMockData();
    await delay();

    const player = mockPlayers.find(p => p.id === playerId);
    if (!player) {
      throw new Error('Player not found');
    }

    const team = mockTeams.find(t => t.playerIds.includes(playerId));

    return {
      player: { ...player, playerId: player.id },
      team: team ? { ...team, teamId: team.id } : null,
      stats: {
        skillLevel: player.skillLevel,
        // Mock stats
        matchesPlayed: 15,
        matchesWon: 10,
        matchesLost: 5,
        winPercentage: 66.7,
      },
    };
  }

  const response = await api.get(`/public/players/${playerId}`);
  return response;
};

/**
 * Get fixtures/matches for a competition and division
 * @param {string} competitionId - Competition ID
 * @param {string} divisionId - Division ID
 * @param {string} status - Match status ('scheduled' or 'completed')
 * @param {string} [month] - Month filter in YYYY-MM format
 * @returns {Promise<{matches: Array, count: number}>}
 */
export const getFixtures = async (competitionId, divisionId, status = 'scheduled', month = null) => {
  if (USE_MOCK_DATA) {
    const { mockMatches, mockTeams } = await getMockData();
    await delay();

    let matches = mockMatches.filter(
      m => m.competitionId === competitionId &&
      m.division === divisionId &&
      m.status === status
    );

    // Filter by month if provided
    if (month) {
      matches = matches.filter(m => {
        const matchMonth = m.scheduledDate.slice(0, 7);
        return matchMonth === month;
      });
    }

    // Add team names to matches
    matches = matches.map(match => {
      const team1 = mockTeams.find(t => t.id === match.team1Id);
      const team2 = mockTeams.find(t => t.id === match.team2Id);

      return {
        ...match,
        matchId: match.id,
        team1Name: team1?.name || 'Unknown Team',
        team2Name: team2?.name || 'Unknown Team',
      };
    });

    // Sort by date
    matches.sort((a, b) => {
      const dateA = new Date(a.scheduledDate);
      const dateB = new Date(b.scheduledDate);
      return status === 'scheduled' ? dateA - dateB : dateB - dateA;
    });

    return {
      matches,
      count: matches.length,
    };
  }

  const params = new URLSearchParams({ divisionId, status });
  if (month) {
    params.append('month', month);
  }
  const response = await api.get(`/public/competitions/${competitionId}/fixtures?${params}`);
  return response;
};

export default {
  getCompetitions,
  getCompetitionDetails,
  getStandings,
  getMatchDetails,
  getTeamProfile,
  getPlayerStats,
  getFixtures,
};
